import React, { useMemo } from 'react';

import {
  ServiceReportDocument,
  buildReportPreviewRegions,
  getServiceReportBodyValue,
  getServiceReportFieldValue,
  getServiceReportSignatureValue
} from '../utils/serviceReportTemplate';

type RepairServiceReportModalProps = {
  open: boolean;
  document: ServiceReportDocument | null;
  saving: boolean;
  regenerating?: boolean;
  sending?: boolean;
  error: string | null;
  mode: 'create' | 'view';
  canRegenerate?: boolean;
  canSend?: boolean;
  regenerateLabel?: string;
  saveLabel?: string;
  sendLabel?: string;
  subtitle?: string;
  hint?: string;
  onClose: () => void;
  onSave: () => void;
  onRegenerate?: () => void;
  onSend?: () => void;
};

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    zIndex: 1100
  },
  modal: {
    width: 'min(1040px, 96vw)',
    maxHeight: '92vh',
    overflowY: 'auto',
    background: '#ffffff',
    borderRadius: '1.25rem',
    boxShadow: '0 28px 90px -36px rgba(15, 23, 42, 0.55)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1.5rem 1.75rem'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#111827'
  },
  meta: {
    color: '#6b7280',
    fontSize: '0.9rem',
    marginTop: '0.25rem'
  },
  hint: {
    color: '#475569',
    fontSize: '0.9rem',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '0.85rem',
    padding: '0.8rem 1rem'
  },
  buttonRow: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  secondaryButton: {
    border: '1px solid #cbd5e1',
    background: '#ffffff',
    color: '#1f2937',
    borderRadius: '9999px',
    padding: '0.7rem 1.4rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  primaryButton: {
    border: 'none',
    background: 'linear-gradient(90deg, #1d4ed8 0%, #2563eb 55%, #1e3a8a 100%)',
    color: '#ffffff',
    borderRadius: '9999px',
    padding: '0.72rem 1.6rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  error: {
    color: '#b91c1c',
    fontSize: '0.9rem'
  },
  pageSurface: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '1rem',
    padding: '1rem'
  },
  page: {
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    minHeight: '1120px',
    borderRadius: '0.35rem',
    padding: '1.25rem',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gridTemplateRows: 'repeat(5, minmax(120px, auto))',
    gap: '0.9rem'
  },
  slot: {
    border: '1px solid #dbeafe',
    borderRadius: '0.75rem',
    padding: '0.9rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem',
    background: '#ffffff',
    minWidth: 0
  },
  blockTitle: {
    fontSize: '0.98rem',
    fontWeight: 700,
    color: '#111827'
  },
  fieldRow: {
    fontSize: '0.92rem',
    lineHeight: 1.45,
    color: '#1f2937',
    wordBreak: 'break-word'
  },
  fieldLabel: {
    fontWeight: 700
  },
  reportBody: {
    fontSize: '0.96rem',
    lineHeight: 1.65,
    color: '#111827',
    whiteSpace: 'pre-wrap',
    textAlign: 'left'
  },
  signatureHint: {
    fontSize: '0.88rem',
    color: '#475569'
  },
  signatureLine: {
    marginTop: 'auto',
    paddingTop: '1.5rem',
    borderBottom: '1px solid #94a3b8',
    minHeight: '2.2rem',
    fontSize: '0.9rem',
    color: '#475569'
  }
};

const RepairServiceReportModal: React.FC<RepairServiceReportModalProps> = ({
  open,
  document,
  saving,
  regenerating = false,
  sending = false,
  error,
  mode,
  canRegenerate = false,
  canSend = false,
  regenerateLabel,
  saveLabel,
  sendLabel,
  subtitle,
  hint,
  onClose,
  onSave,
  onRegenerate,
  onSend
}) => {
  const template = document?.template || null;
  const previewRegions = useMemo(() => buildReportPreviewRegions(template), [template]);
  const signatureData = (document?.data?.signature || {}) as Record<string, unknown>;
  const serviceRepresentativeNote =
    typeof signatureData.serviceRepresentativeNote === 'string'
      ? signatureData.serviceRepresentativeNote.trim()
      : '';

  if (!open || !document) return null;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={event => event.stopPropagation()}>
        <div style={styles.toolbar}>
          <div>
            <div style={styles.title}>{document.title || template?.name || 'Service Report'}</div>
            <div style={styles.meta}>
              {subtitle ||
                `Template: ${template?.name || 'Primary Service Report'} | ${
                  mode === 'view'
                    ? 'Locked saved repair report snapshot'
                    : 'Preview of the report to be saved'
                }`}
            </div>
          </div>
          <div style={styles.buttonRow}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={onClose}
              disabled={saving || regenerating || sending}
            >
              Close
            </button>
            {mode === 'view' && onRegenerate && (
              <button
                type="button"
                style={{
                  ...styles.secondaryButton,
                  ...(!canRegenerate || regenerating || sending || saving ? styles.buttonDisabled : {})
                }}
                onClick={onRegenerate}
                disabled={!canRegenerate || regenerating || sending || saving}
              >
                {regenerating ? 'Building report...' : regenerateLabel || 'Regenerate report'}
              </button>
            )}
            {mode === 'view' && onSend && (
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...(!canSend || sending || regenerating || saving ? styles.buttonDisabled : {})
                }}
                onClick={onSend}
                disabled={!canSend || sending || regenerating || saving}
              >
                {sending ? 'Sending report...' : sendLabel || 'Send report'}
              </button>
            )}
            {mode === 'create' && (
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...(saving ? styles.buttonDisabled : {})
                }}
                onClick={onSave}
                disabled={saving}
              >
                {saving ? 'Saving report...' : saveLabel || 'Create report'}
              </button>
            )}
          </div>
        </div>

        <div style={styles.hint}>
          {hint ||
            (mode === 'view'
              ? 'This is the locked saved service-report snapshot linked to this task and case.'
              : 'Saving writes a locked JSON service-report snapshot linked permanently to this task and case. Creating it again later overwrites the same linked report record.')}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.pageSurface}>
          <div style={styles.page}>
            {previewRegions.map(region => {
              const block = region.block;
              return (
                <div
                  key={region.id}
                  style={{
                    ...styles.slot,
                    ...(block ? null : { border: 'none', background: 'transparent' }),
                    gridColumn: `${region.colStart} / ${region.colEnd + 1}`,
                    gridRow: `${region.rowStart} / ${region.rowEnd + 1}`,
                    justifyContent: region.rowEnd === 5 ? 'flex-end' : 'flex-start',
                    textAlign:
                      region.colStart === 1 && region.colEnd === 1
                        ? 'left'
                        : region.colStart === 3 && region.colEnd === 3
                        ? 'right'
                        : 'center'
                  }}
                >
                  {!block ? null : (
                    <>
                      <div style={styles.blockTitle}>{block.label}</div>
                      {block.kind === 'report_body' ? (
                        <div style={styles.reportBody}>
                          {getServiceReportBodyValue(document, block)}
                        </div>
                      ) : block.kind === 'signature' ? (
                        <>
                          <div style={styles.signatureHint}>
                            {block.label.toLowerCase().includes('customer')
                              ? block.signatureMode === 'print_only'
                                ? 'Print and sign manually'
                                : 'Sign on-screen or print and sign'
                              : serviceRepresentativeNote ||
                                (block.signatureMode === 'print_only'
                                  ? 'Print and sign manually'
                                  : 'Sign on-screen or print and sign')}
                          </div>
                          <div style={styles.signatureLine}>
                            {getServiceReportSignatureValue(document, block)}
                          </div>
                        </>
                      ) : (
                        block.fields
                          .filter(field => field.included)
                          .map(field => (
                            <div key={field.id} style={styles.fieldRow}>
                              <span style={styles.fieldLabel}>{field.label}: </span>
                              <span>{getServiceReportFieldValue(document, field)}</span>
                            </div>
                          ))
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairServiceReportModal;
